/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { AddSharp, Download, Edit, Search } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Button,
  InputAdornment,
  Snackbar,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Warehouse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { InventoryPartData } from "../../../@types/DRO/GetPartsListResponse";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { getCurrentAuthority } from "../../../utils/permissions";
import { useLoading } from "../../../views/Loader/LoadingContext";
import InventoryUpdateModal from "../../Layouts/AppModals/InventoryUpdateModal";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";
import InventoryTableComponent from "../../Layouts/Table/InventoryTableComponent";
import RoleGuard from "../../auth/RoleGuard";

const InventoryList = () => {
  const { id } = useParams();

  const columns = [
    "partId",
    "partName",
    "referenceCode",
    "partProductName",
    "price",
    "costPrice",
    "quantityOnHand",
    "changeQuantity",
    "backOrders",
    "inTransit",
    "underPickup",
    "freeToUse",
    "incoming",
    "outgoing",
  ];

  const columnHeaders = [
    "Product ID",
    "Part Name",
    "Reference Code",
    "Product Name",
    "Sales Price",
    "Cost Price",
    "Quantity On Hand",
    "Change Quantity",
    "Back Orders",
    "In Transit",
    "Under Pickup",
    "Free To Use",
    "Incoming",
    "Outgoing",
  ];

  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const { setLoading } = useLoading();
  const [isDownloading, setIsDownloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "error"
  );

  const [parts, setParts] = useState<InventoryPartData[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<{
    id: number;
    status: string;
  } | null>(null);

  const [typeFilter, setTypeFilter] = useState("");
  const [selectedHubs, setSelectedHubs] = useState<Warehouse[]>([]);
  const [hubs, setHubs] = useState<Warehouse[]>([]);
  const [hubPage, setHubPage] = useState(0);
  const [hubSearchQuery, setHubSearchQuery] = useState("");
  const [hasMoreHubs, setHasMoreHubs] = useState(true);
  const [isLoadingHubs, setIsLoadingHubs] = useState(false);
  const hubAutocompleteRef = useRef<any>(null);

  const [changeQuantities, setChangeQuantities] = useState<{
    [partId: number]: string;
  }>({});
  const [loadingRows, setLoadingRows] = useState<{ [partId: number]: boolean }>(
    {}
  );
  const [successRows, setSuccessRows] = useState<{ [partId: number]: boolean }>(
    {}
  );
  const [errorRows, setErrorRows] = useState<{ [partId: number]: string }>({});

  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchParts = useCallback(
    async (resetPage = false) => {
      setLoading(true);
      try {
        const selectedHubIds = selectedHubs.map((hub) => hub.id);
        const response = await AllPartApi.api.getInventoryPartList(
          resetPage ? 0 : page,
          rowsPerPage,
          searchQuery,
          selectedHubIds.length > 0 ? selectedHubIds : undefined,
          typeFilter || undefined
        );
        setParts(response.data.data);
        setTotalPages(response.data.totalPages);
        if (resetPage) {
          setPage(0);
        }
      } catch (error) {
        console.error("Error fetching parts", error);
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchQuery, selectedHubs, typeFilter, setLoading]
  );

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const fetchHubs = useCallback(
    async (pageNumber: number, search: string, reset: boolean = false) => {
      if (isLoadingHubs && !reset) return;

      setIsLoadingHubs(true);
      try {
        const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
          pageNumber,
          rowsPerPage,
          search
        );

        if (reset) {
          setHubs(response.data.data);
          setHubPage(0);
          setHasMoreHubs(response.data.data.length === rowsPerPage);
        } else {
          setHubs((prev) => [...prev, ...response.data.data]);
          setHubPage(pageNumber);
          setHasMoreHubs(response.data.data.length === rowsPerPage);
        }
      } catch (error) {
        console.error("Error fetching warehouses", error);
      } finally {
        setIsLoadingHubs(false);
      }
    },
    [rowsPerPage, isLoadingHubs]
  );

  const debouncedHubSearch = useCallback(
    debounce((searchValue: string) => {
      fetchHubs(0, searchValue, true);
    }, 500),
    [fetchHubs]
  );

  const handleHubScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    const threshold = listboxNode.scrollHeight - 10;

    if (position >= threshold && hasMoreHubs && !isLoadingHubs) {
      const nextPage = hubPage + 1;
      fetchHubs(nextPage, hubSearchQuery);
    }
  };

  // FIX: was only sending `selectedHub` (single id, null unless exactly
  // one hub picked) — multi-hub selections were silently ignored on
  // report download. Now sends all selected hub ids.
  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      const selectedHubIds = selectedHubs.map((hub) => hub.id);
      await AllPartApi.api.getInventoryPartListReportDownload(
        selectedHubIds.length > 0 ? selectedHubIds : undefined,
        searchQuery || undefined,
        typeFilter || undefined
      );
      setSnackbarMessage("Report downloaded successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Error downloading report", error);
      setSnackbarMessage(
        error?.message ||
          error?.response?.data?.message ||
          "Failed to download report. Please try again."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleChangeQuantityInput = (partId: number, value: string) => {
    setChangeQuantities((prev) => ({ ...prev, [partId]: value }));
    setSuccessRows((prev) => ({ ...prev, [partId]: false }));
    setErrorRows((prev) => ({ ...prev, [partId]: "" }));
  };

  const handleUpdateQuantity = async (
    partId: number,
    warehouseId: number,
    currentQuantity: number
  ) => {
    const value = changeQuantities[partId];
    if (!value || isNaN(Number(value))) return;
    setLoadingRows((prev) => ({ ...prev, [partId]: true }));
    setSuccessRows((prev) => ({ ...prev, [partId]: false }));
    setErrorRows((prev) => ({ ...prev, [partId]: "" }));
    try {
      await AllPartApi.api.warehouseStockUpdate(
        warehouseId,
        partId,
        Number(value)
      );
      setSuccessRows((prev) => ({ ...prev, [partId]: true }));
      setParts((prevParts) =>
        prevParts.map((p) =>
          p.partId === partId ? { ...p, quantityOnHand: Number(value) } : p
        )
      );
      setChangeQuantities((prev) => ({ ...prev, [partId]: "" }));
    } catch (error: any) {
      setErrorRows((prev) => ({
        ...prev,
        [partId]: error?.message || "Failed to update",
      }));
    } finally {
      setLoadingRows((prev) => ({ ...prev, [partId]: false }));
    }
  };

  const handleModalUpdate = async (
    warehouseId: number,
    partId: number,
    quantity: number,
    partCondition?: string
  ) => {
    try {
      await AllPartApi.api.warehouseStockUpdate(warehouseId, partId, quantity, partCondition);
      await fetchParts(true);
    } catch (error: any) {
      throw new Error(error?.message || "Failed to update inventory");
    }
  };

  useEffect(() => {
    fetchHubs(0, "", true);
  }, []);

  useEffect(() => {
    fetchParts();
  }, [page, rowsPerPage, searchQuery, selectedHubs, typeFilter]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleHubChange = (event: any, newValue: Warehouse[]) => {
    setSelectedHubs(newValue);
    setPage(0);
  };

  const handleViewClick = (partId: number) => {
    navigate(`/parts/${partId}`);
  };

  const selectedHub = selectedHubs.length === 1 ? selectedHubs[0].id : null;
  const restrictedRoles = ["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"];
  const currentAuthority = getCurrentAuthority();
  const isRestrictedRole =
    currentAuthority !== null && restrictedRoles.includes(currentAuthority);
  const showChangeQuantity = selectedHub !== null && !isRestrictedRole;
  const filteredColumns = showChangeQuantity
    ? columns.filter((col) => !isRestrictedRole || col !== "price")
    : columns.filter(
        (col) => col !== "changeQuantity" && (!isRestrictedRole || col !== "price")
      );
  const filteredColumnHeaders = showChangeQuantity
    ? columnHeaders.filter(
        (header) => !isRestrictedRole || header !== "Sales Price"
      )
    : columnHeaders.filter(
        (header) =>
          header !== "Change Quantity" &&
          (!isRestrictedRole || header !== "Sales Price")
      );

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="w-64 flex-shrink-0 bg-white shadow-md">
        <SideBarNavigation />
      </div>
      <div className="flex-1 overflow-auto">
        <div className="p-6 bg-[#F8FAFC] min-h-full">
          {/* Header with Products title and Download Report button */}
          <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
            <h1 className="text-3xl font-semibold text-left">Products</h1>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Type:</span>
                <select
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(0);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="">ALL</option>
                  <option value="WORKING">Working</option>
                  <option value="NOT_WORKING">Not Working</option>
                </select>
              </div>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadReport}
                disabled={isDownloading}
                sx={{
                  textTransform: "none",
                  borderColor: "#171F21",
                  color: "#171F21",
                  "&:hover": {
                    borderColor: "#374151",
                    backgroundColor: "rgba(23, 31, 33, 0.04)",
                  },
                  fontSize: "14px",
                  borderRadius: "8px",
                  px: 2.5,
                  py: 1,
                }}>
                {isDownloading ? "Downloading..." : "Download Report"}
              </Button>
            </div>
          </div>

          {/* FIX: flex-wrap so the row drops to a new line instead of
              overflowing/squishing once the hub Autocomplete grows */}
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Hub Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">Hub :</span>
                <Autocomplete
                  ref={hubAutocompleteRef}
                  multiple
                  limitTags={2}
                  options={hubs}
                  getOptionLabel={(option) => option.name}
                  value={selectedHubs}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={handleHubChange}
                  onInputChange={(event, newInputValue) => {
                    setHubSearchQuery(newInputValue);
                    debouncedHubSearch(newInputValue);
                  }}
                  loading={isLoadingHubs}
                  ListboxProps={{
                    onScroll: handleHubScroll,
                    style: { maxHeight: 200, overflow: "auto" },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="All Hubs"
                      sx={{
                        bgcolor: "white",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#e5e7eb",
                          },
                          "&:hover fieldset": {
                            borderColor: "#d1d5db",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3b82f6",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    minWidth: 200,
                    maxWidth: 320,
                  }}
                />
              </div>

              {/* Search Bar */}
              <TextField
                placeholder="Search by part name or ID"
                value={searchQuery}
                onChange={handleSearchChange}
                size="small"
                sx={{
                  width: 300,
                  bgcolor: "white",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover fieldset": {
                      borderColor: "#d1d5db",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Update Inventory Button */}
              <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER","ROLE_ERP_FRANCHISE_ADMIN"]}>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setIsModalOpen(true)}
                  sx={{
                  textTransform: "none",
                  borderColor: "#171F21",
                  color: "#171F21",
                  "&:hover": {
                    borderColor: "#374151",
                    backgroundColor: "rgba(23, 31, 33, 0.04)",
                  },
                  fontSize: "14px",
                  borderRadius: "8px",
                  px: 2.5,
                  py: 1,
                }}>
                Update Inventory
              </Button>
              </RoleGuard>
            </div>

            {/* Add Product Button */}
            <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER","ROLE_ERP_FRANCHISE_ADMIN"]}>
            <Link to="/create/parts">
              <Button
                variant="contained"
                startIcon={<AddSharp />}
                sx={{
                  textTransform: "none",
                  bgcolor: "#171F21",
                  "&:hover": { bgcolor: "#374151" },
                  fontSize: "16px",
                  borderRadius: "30px",
                  px: 3,
                  py: 1,
                }}>
                Add Product
              </Button>
            </Link>
            </RoleGuard>
          </div>

          <div className="overflow-x-auto">
            <InventoryTableComponent
              columns={filteredColumns}
              columnHeaders={filteredColumnHeaders}
              data={parts}
              setSelectedOrder={setSelectedOrder}
              currentPage={page}
              setCurrentPage={setPage}
              totalPages={totalPages}
              onViewClick={handleViewClick}
              changeQuantities={changeQuantities}
              loadingRows={loadingRows}
              successRows={successRows}
              errorRows={errorRows}
              onChangeQuantityInput={handleChangeQuantityInput}
              onUpdateQuantity={handleUpdateQuantity}
              selectedHub={selectedHub}
            />
          </div>
        </div>
      </div>

      <InventoryUpdateModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpdate={handleModalUpdate}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default InventoryList;